import React, { useState, useEffect, useCallback, useRef } from "react";
import _ from "lodash";
import axios from "axios";
import {
  makeStyles,
  IconButton,
  InputAdornment,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TablePagination,
  Checkbox,
  Box,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Card from "../../components/Card/Card";
import CardHeader from "../../components/Card/CardHeader";
import CardBody from "../../components/Card/CardBody";
import CustomInput from "../../components/CustomInput/CustomInput";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import People from "@material-ui/icons/People";
import CategoryMastersAdd from "./CategoryMastersAdd";
import CategoryMastersEdit from "./CategoryMastersEdit";
import {
  DeleteCategorysAPI,
  GetAllCategorysAPI,
  baseUrl,
} from "../../config/api";
import { ConfirmationDialog } from "../../components/common";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ImportExcel } from "../Master/ImportExcel";
import { CloudDownload, CloudUpload } from "@material-ui/icons";
import * as XLSX from 'xlsx';
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  imageSize: {
    maxWidth: 150,
    maxHeight: 100,
  },
  imageWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const columns = [
  {
    id: "name",
    label: "Name",
    format: (value) => (value ? value : "NA"),
  },
  {
    id: "categoryImage",
    label: "Category Image",
    format: (value) => (value ? value : "NA"),
  },

  { id: "action", label: "Action" },
];

export default function CategoryMastersList(props) {
  const classes = useStyles();
  const userInfo = useSelector((state) => state.userReducer.userInfo);
  const authToken = useSelector((state) => state.userReducer.authToken);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(null);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [categorys, setCategorys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const hasInitialLoad = useRef(false);

  const _getCategorys = useCallback(async (pageNum, rowsPerPageNum, search = "") => {
    try {
      setLoading(true);
      const skip = pageNum * rowsPerPageNum;
      let query = `&limit=${rowsPerPageNum}&skip=${skip}`;
      
      if (search) {
        query += `&name=${encodeURIComponent(search)}`;
      }

      const res = await axios.get(GetAllCategorysAPI(userInfo.entity) + query, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      setCategorys(res.data.categoryMasters || res.data.categories || []);
      setTotal(res.data.total || res.data.categoryMasters?.length || 0);
    } catch (err) {
      toast.error('Cannot fetch data', {
        position: "top-center",
        autoClose: 1500,
      });
      setCategorys([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userInfo.entity, authToken]);

  // Initial load: only on mount
  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      _getCategorys(0, rowsPerPage, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for pagination changes
  useEffect(() => {
    if (!hasInitialLoad.current) {
      return;
    }
    _getCategorys(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Debounced search effect
  useEffect(() => {
    if (!hasInitialLoad.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        setPage(0);
        _getCategorys(0, rowsPerPage, searchTerm);
      } else {
        setPage(0);
        _getCategorys(0, rowsPerPage, "");
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Function to refresh data (called explicitly after successful operations)
  const refreshCategorys = useCallback(() => {
    _getCategorys(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, _getCategorys]);

  async function _deleteItem(item) {
    try {
      await axios.delete(DeleteCategorysAPI(item._id), {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      toast.success('Successfully Deleted!', {
        position: "top-center",
        autoClose: 1500,
      });
      refreshCategorys();
    } catch (e) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
      });
    }
  }

  const handleOpenConfirmationModal = (object) => {
    setOpenConfirmationModal(object);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function closeAddDialog() {
    setOpenAddDialog(false);
  }

  function closeEditDialog() {
    setOpenEditDialog(null);
  }

  // Server-side search is now handled in API call
  const data = categorys;

  // Export function
  const handleExport = async () => {
    setIsExporting(true);
    const loadingToastId = toast.loading('🔄 Preparing export... Please wait', {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });

    try {
      // First, get the total count
      toast.update(loadingToastId, {
        render: '📊 Fetching total record count...',
        type: 'info',
        isLoading: true,
      });

      const totalRes = await axios.get(GetAllCategorysAPI(userInfo.entity) + `&limit=1&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const total = totalRes.data.total || 0;
      
      if (total === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      // Fetch all data using the total count
      toast.update(loadingToastId, {
        render: `📥 Downloading ${total} records... This may take a moment`,
        type: 'info',
        isLoading: true,
      });

      const res = await axios.get(GetAllCategorysAPI(userInfo.entity) + `&limit=${total}&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const allData = res.data.categoryMasters || res.data.categories || [];
      if (!allData || allData.length === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      toast.update(loadingToastId, {
        render: '⚙️ Processing data for export...',
        type: 'info',
        isLoading: true,
      });

      const exportHeadings = [
        { id: "name", label: "Name" },
        { id: "categoryImage", label: "Category Image" }
      ];

      const exportData = allData.map(row => {
        const rowData = {};
        exportHeadings.forEach(col => {
          let value = row[col.id];
          rowData[col.id] = value;
        });
        return rowData;
      });

      toast.update(loadingToastId, {
        render: '💾 Generating Excel file...',
        type: 'info',
        isLoading: true,
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Categories');
      XLSX.writeFile(workbook, 'categories_export.xlsx');

      toast.update(loadingToastId, {
        render: `✅ Successfully exported ${allData.length} records to Excel!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.update(loadingToastId, {
        render: `❌ Export failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        style: {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 className={classes.cardTitleWhite}>Category</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                }}
              >
                <CustomInput
                  id="search"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    style: {
                      marginTop: 0,
                      marginRight: 10,
                      color: "white",
                    },
                    placeholder: "Search",
                    value: searchTerm,
                    onChange: (e) => setSearchTerm(e.target.value),
                    endAdornment: (
                      <InputAdornment position="end">
                        <People />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  aria-label="add"
                  style={{ color: "white" }}
                  onClick={() => setOpenAddDialog(true)}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <Checkbox
                    style={{
                      color: "#512da8",
                    }}
                    onChange={(e) => {
                      let c = [];
                      if (e.target.checked) {
                        data.map((i) => {
                          c.push(i._id);
                        });
                      }
                      setSelectedRows(c);
                    }}
                    checked={
                      selectedRows.length > 0 &&
                      data.length === selectedRows.length
                    }
                    inputProps={{ "aria-labelledby": -1 }}
                  />
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={"center"}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" style={{ padding: "40px" }}>
                      <Box style={{ color: "#666" }}>
                        {searchTerm ? "No categories found matching your search." : "No categories found."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row._id}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            style={{
                              color: "#512da8",
                            }}
                            onChange={() => {
                              let copy = _.clone(selectedRows);
                              if (_.includes(copy, row._id)) {
                                let index = _.indexOf(copy, row._id);
                                copy.splice(index, 1);
                              } else {
                                copy.push(row._id);
                              }
                              setSelectedRows(copy);
                            }}
                            checked={_.includes(selectedRows, row._id)}
                            inputProps={{ "aria-labelledby": row._id }}
                          />
                        </TableCell>
                        {columns.map((column) => {
                          const value = row[column.id];

                          if (column.id === "categoryImage") {
                            return (
                              <TableCell key={column.id} align={"center"}>
                                {row.categoryImage !== "null" && (
                                  <Box className={classes.imageWrapper}>
                                    <img
                                      src={`${baseUrl}${row.categoryImage}`}
                                      className={classes.imageSize}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                            );
                          }

                          if (column.id === "action") {
                            return (
                              <TableCell align={"center"}>
                                <Box display={"flex"} justifyContent={"center"}>
                                  <IconButton
                                    aria-label="edit"
                                    onClick={() =>
                                      setOpenEditDialog({
                                        open: true,
                                        item: row,
                                      })
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleOpenConfirmationModal({
                                        title: "Delete Record",
                                        msg: "Do you want to delete this record?",
                                        onPress: () => _deleteItem(row),
                                      })
                                    }
                                    aria-label="delete"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={"center"}>
                              {column.format ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardBody>
        </Card>
      </GridItem>
      {openAddDialog ? (
        <CategoryMastersAdd 
          open={openAddDialog} 
          close={closeAddDialog}
          fetchData={refreshCategorys}
        />
      ) : null}

      {openEditDialog ? (
        <CategoryMastersEdit
          open={openEditDialog}
          close={closeEditDialog}
          item={openEditDialog.item}
          fetchData={refreshCategorys}
        />
      ) : null}

      {openConfirmationModal ? (
        <ConfirmationDialog
          open={openConfirmationModal}
          onClose={handleCloseConfirmationModal}
          onConfirm={openConfirmationModal.onPress}
          title={"Delete Record"}
          msg={"Do you want to delete this record?"}
        />
      ) : null}
      <ImportExcel
        db={"categoryMasters"}
        open={openImportDialog}
        close={() => {
          setOpenImportDialog(false);
          refreshCategorys();
        }}
      />
      <Backdrop
        open={isExporting}
        style={{
          zIndex: 9999,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={60} thickness={4} style={{ color: '#512da8', marginBottom: 20 }} />
          <Box style={{ fontSize: '18px', fontWeight: 'bold', marginTop: 10 }}>
            Exporting Data...
          </Box>
          <Box style={{ fontSize: '14px', marginTop: 5, opacity: 0.8 }}>
            Please wait while we prepare your Excel file
          </Box>
        </Box>
      </Backdrop>
    </GridContainer>
  );
}
